import {useApplication} from "../../../ApplicationProvider";
import {useNavigate, useParams} from "react-router-dom";
import {useNavigatorFeature} from "../../../useFeature";
import {observer} from "mobx-react";
import {Box, Chip, Typography} from "@mui/material";
import {authorizationStateChipLabelAndColor} from "../../../components/state/AuthorizationStateRenderer";
import PersonIcon from "@mui/icons-material/Person";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import PlaceIcon from "@mui/icons-material/Place";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import GroupIcon from "@mui/icons-material/Group";
import TabsPanel from "../../../components/tabsPanel/TabsPanel";
import ArticleIcon from '@mui/icons-material/Article';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import CustomerOnboardingEditionFeature from "./CustomerOnboardingEditionFeature";
import LayoutContentWrapper from "../../../components/layout/LayoutContentWrapper";
import LayoutContentToolbar from "../../../components/layout/LayoutContentToolbar";
import {CUSTOMER_ONBOARDINGS_PATH} from "../../../routes";
import FormSection from "../../../forms/FormSection";
import LegalEntityPanel from "./LegalEntityPanel";
import CustomerContactPanel from "./CustomerContactPanel";
import CommercialTermsPanel from "./CommercialTermsPanel";
import TaxDataPanel from "./TaxDataPanel";
import FinanceDataPanel from "./FinanceDataPanel";
import UsersPanel from "./UsersPanel";
import DeliverySitesOnboardingRequestsPanel from "./DeliverySitesOnboardingRequestsPanel";
import FilledButton from "../../../components/buttons/FilledButton";
import TaskIcon from "@mui/icons-material/Task";
import * as React from "react";
import CuitFormatter from "../../../utils/CuitRenderer";

function CustomerOnboardingEditionScreen({taxpayerIdentificationNumber: taxpayerIdentificationNumberProp, showWorkflowButtons = true}) {
    const application = useApplication();
    const navigator = useNavigate();
    const {taxpayerIdentificationNumber: taxpayerIdentificationNumberFromParams} = useParams();
    const taxpayerIdentificationNumber = taxpayerIdentificationNumberProp || taxpayerIdentificationNumberFromParams;
    const feature = useNavigatorFeature(
        () => new CustomerOnboardingEditionFeature(application, navigator, taxpayerIdentificationNumber),
        navigator,
        [taxpayerIdentificationNumber]
    );

    const isFeatureReady = (feature) => {
        return feature && feature.loaded
    }

    const _legalEntityPanel = () => {
        return <LegalEntityPanel generalFeature={feature}/>;
    }

    const _contactPanel = () => {
        return <CustomerContactPanel generalFeature={feature}/>
    }

    const _deliverySitesOnboardingRequestsPanel = () => {
        return <DeliverySitesOnboardingRequestsPanel generalFeature={feature}/>;
    }

    const _commercialConditionsPanel = () => {
        return (
            <CommercialTermsPanel generalFeature={feature}/>
        );
    }

    const _usersPanel = () => {
        return <UsersPanel generalFeature={feature}/>;
    }

    const _taxDataPanel = () => {
        return (
            <TaxDataPanel generalFeature={feature}/>
        );
    }

    const _financeDataPanel = () => {
        return (
            <FinanceDataPanel generalFeature={feature}/>
        );
    }

    const tabs = [
        {
            label: () => "Cliente",
            title: () => "",
            icon: () => <PersonIcon/>,
            style: () => null,
            panel: () => _legalEntityPanel(),

        },
        {
            label: () => "Contacto",
            title: () => "",
            icon: () => <ContactPhoneIcon/>,
            style: () => null,
            panel: () => _contactPanel(),
        },
        {
            label: () => "Puntos de entrega",
            title: () => "",
            icon: () => <PlaceIcon/>,
            style: () => null,
            panel: () => _deliverySitesOnboardingRequestsPanel(),
        },
        {
            label: () => "Cond. Comerciales",
            title: () => "",
            icon: () => <RequestQuoteIcon/>,
            style: () => null,
            panel: () => _commercialConditionsPanel(),
        },
        {
            label: () => "Usuarios",
            title: () => "",
            icon: () => <GroupIcon/>,
            style: () => null,
            panel: () => _usersPanel(),
        },
        {
            label: () => "Info. Impositiva",
            title: () => "",
            icon: () => <ArticleIcon/>,
            style: () => null,
            panel: () => _taxDataPanel(),
        },
        {
            label: () => "Cuenta Corriente",
            title: () => "",
            icon: () => <AssuredWorkloadIcon/>,
            style: () => null,
            panel: () => _financeDataPanel(),
        }
    ];

    const tabPermissions =
        {
            "Cliente": ['CLOSER_PERMISSION', 'COMMERCIAL_PERMISSION',
                'LOGISTICS_PERMISSION', 'TAX_PERMISSION',
                'FINANCE_PERMISSION', 'LOGISTICS_AUTHORIZATION_PERMISSION',
                'FINANCE_AUTHORIZATION_PERMISSION', 'COMMERCIAL_AUTHORIZATION_PERMISSION',
                'TAX_AUTHORIZATION_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION'],
            "Contacto": ['CLOSER_PERMISSION', 'COMMERCIAL_PERMISSION',
                'ONBOARDINGS_ERP_IMPORTER_PERMISSION'],
            "Puntos de entrega": ['CLOSER_PERMISSION', 'LOGISTICS_PERMISSION',
                'TAX_PERMISSION', 'LOGISTICS_AUTHORIZATION_PERMISSION',
                'TAX_AUTHORIZATION_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION',
                'COMMERCIAL_PERMISSION'],
            "Cond. Comerciales": ['CLOSER_PERMISSION', 'COMMERCIAL_PERMISSION',
                'COMMERCIAL_AUTHORIZATION_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION'],
            "Usuarios": ['CLOSER_PERMISSION', 'COMMERCIAL_PERMISSION',
                'ONBOARDINGS_ERP_IMPORTER_PERMISSION'],
            "Info. Impositiva": ['CLOSER_PERMISSION', 'TAX_PERMISSION',
                'TAX_AUTHORIZATION_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION',
                'COMMERCIAL_PERMISSION'],
            "Cuenta Corriente": ['CLOSER_PERMISSION', 'FINANCE_PERMISSION',
                'FINANCE_AUTHORIZATION_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION',
                'COMMERCIAL_PERMISSION']
        }
    const filteredTabs = tabs.filter(tab =>(tabPermissions[tab.label()]).some(permission => application.permissions().includes(permission)));

    const renderAuthorizationObservations = () => {
        if (!feature.hasAuthorizationObservations()) {
            return null;
        }
        return (
            <FormSection>
                <Typography variant="subtitle1" sx={{marginBottom: 2}}>Observaciones de las autorizaciones</Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: 'max-content max-content 1fr', columnGap: 2, rowGap: 1.5, alignItems: 'start'}}>
                    {feature.authorizationObservationRows().map(row => {
                        const {label, color} = authorizationStateChipLabelAndColor(row.state);
                        return (
                            <React.Fragment key={row.area}>
                                <Typography>{row.area}</Typography>
                                <Typography component="div" sx={{display: 'flex', alignItems: 'center', height: (theme) => `${theme.typography.body1.lineHeight}em`}}>
                                    <Chip label={label} color={color} size="small"/>
                                </Typography>
                                <Typography>{row.observations}</Typography>
                            </React.Fragment>
                        );
                    })}
                </Box>
            </FormSection>
        );
    }

    const renderContent = () => {
        return (
            <FormSection>
                <TabsPanel tabs={filteredTabs}
                           variant="scrollable"/>
            </FormSection>
        )
    }


    const renderWorkflowButton = () => {
        const buttonText = feature.isProcessingByArea() ? 'Solicitar Autorización' : 'Finalizar Etapa';
        return <>
            {showWorkflowButtons && feature.canAdvanceCustomerOnboarding() &&
                <div style={{alignItems: 'right', display: 'flex', justifyContent: 'right'}}>
                    <FilledButton onClick={() => feature.advanceCustomerOnboarding()}>
                        <TaskIcon fontSize="medium"/>
                        {buttonText}
                    </FilledButton>
                </div>}
        </>;
    }

    const cuitFormatter = new CuitFormatter();
    return <>
            <LayoutContentWrapper isLoading={!isFeatureReady(feature)} renderContent={() => (
                <Box>
                    <LayoutContentToolbar
                        title={`Alta de Cliente: ${cuitFormatter.format(feature.taxpayerIdentificationNumber)}`}
                        breadcrumbs={[
                            {label: 'Altas de Clientes', path: CUSTOMER_ONBOARDINGS_PATH},
                            {label: cuitFormatter.format(feature.taxpayerIdentificationNumber)},
                        ]}
                    />
                    {renderAuthorizationObservations()}
                    {renderContent()}
                    {renderWorkflowButton()}
                </Box>
            )}/>
        </>
}


export default observer(CustomerOnboardingEditionScreen)
